import { useState, useRef, useCallback, useLayoutEffect, useImperativeHandle, forwardRef, KeyboardEvent, ReactNode } from 'react';
import { ArrowUp, Square } from 'lucide-react';

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  onDraftChange?: (hasText: boolean) => void;
  /** Dentro de la caja, junto al botón de enviar (el selector de modo). */
  controls?: ReactNode;
  /** Dentro de la caja, a la izquierda de la fila (el aviso de cobertura). */
  leading?: ReactNode;
}

/** Lo que el chat puede pedirle a la caja desde fuera: llenarla con un ejemplo sin enviarlo (DESIGN.md §12). */
export interface ChatInputHandle {
  fill: (text: string) => void;
  /** Vacía la caja (al abrir otra conversación o una nueva). */
  clear: () => void;
}

const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput({ onSend, onStop, disabled, isStreaming, onDraftChange, controls, leading }, ref) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Cuenta las veces que se llenó desde fuera: así el efecto corre aunque el texto sea el mismo que ya había.
  const [fills, setFills] = useState(0);

  useImperativeHandle(ref, () => ({
    fill: (text: string) => {
      setValue(text);
      onDraftChange?.(text.trim().length > 0);
      setFills(n => n + 1);
    },
    clear: () => {
      setValue('');
      onDraftChange?.(false);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    },
  }), [onDraftChange]);

  // Tras llenarla desde fuera: alto según el texto y foco con el cursor al final.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!fills || !el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [fills]);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    onDraftChange?.(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, disabled, onSend, onDraftChange]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  };

  return (
    // Desvanecido blanco sobre el compositor (DESIGN.md §2.1, uso funcional permitido), como en la maqueta.
    <div className="relative flex-shrink-0 bg-white px-4 py-3 before:content-[''] before:absolute before:inset-x-0 before:-top-7 before:h-7 before:bg-gradient-to-b before:from-white/0 before:to-white before:pointer-events-none">
      <div className="max-w-3xl mx-auto">
      {/* Compositor (maqueta 2026-10-14-modos-respuesta): el campo y, dentro de la misma caja, la fila con los controles
          (selector de modo) y el botón de enviar. */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] px-4 pt-3.5 pb-2 transition-colors motion-reduce:transition-none focus-within:border-indigo-200">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => { setValue(e.target.value); onDraftChange?.(e.target.value.trim().length > 0); }}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder="Escribe tu consulta..."
          disabled={disabled || isStreaming}
          rows={1}
          aria-label="Escribe tu consulta"
          data-gramm="false"
          data-gramm_editor="false"
          data-enable-grammarly="false"
          autoComplete="off"
          className="block w-full resize-none border-0 bg-transparent p-0 text-sm leading-[22px] text-gray-900 focus:outline-none focus:ring-0 disabled:text-gray-400 placeholder:text-gray-400"
          style={{ maxHeight: '120px' }}
        />
        <div className="flex items-center gap-1.5 mt-2">
          {leading && <div className="flex items-center text-slate-500">{leading}</div>}
          <div className="flex-1" />
          {controls}
          {isStreaming ? (
            <button
              onClick={onStop}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors motion-reduce:transition-none focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              title="Detener"
              aria-label="Detener"
            >
              <Square className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!value.trim() || disabled}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white transition-colors motion-reduce:transition-none focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-slate-300 disabled:cursor-not-allowed"
              title="Enviar"
              aria-label="Enviar"
            >
              <ArrowUp className="w-4 h-4" strokeWidth={1.8} />
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-500 text-center mt-2">
        AlertAI usa inteligencia artificial y puede cometer errores. Verifica las citas contra la fuente original.
      </p>
      </div>
    </div>
  );
});

export default ChatInput;
